import React, { useEffect, useRef, useState } from 'react';
import { FixedFooterLayoutProps } from './interface';
import { FixedFooter } from './InternalFixedFooter.web';
import { FixedTitle } from './InternalFixedTitle.web';

const TOP_BAR_TEST_ID = 'topbar-root';

export const FixedFooterLayout = ({ children, footerContent, title }: FixedFooterLayoutProps) => {
  const { height: footerHeight, ref: footerRef } = useResponsiveFooterSpacer();
  const titleRef = useRef<HTMLDivElement | null>(null);
  const [titlePadding, setTitlePadding] = useState(0);

  useEffect(() => {
    if (!title || !titleRef.current) {
      return;
    }

    const updatePadding = () => {
      if (!titleRef.current) {
        return;
      }
      const titleBottom = titleRef.current.getBoundingClientRect().bottom;
      const topbarEl = document.querySelector(`[data-testid="${TOP_BAR_TEST_ID}"]`);
      const topbarBottom = topbarEl ? topbarEl.getBoundingClientRect().bottom : 0;
      const topbarMarginBottom = topbarEl
        ? parseFloat(window.getComputedStyle(topbarEl).marginBottom) || 0
        : 0;
      setTitlePadding(Math.max(0, Math.ceil(titleBottom - topbarBottom - topbarMarginBottom) + 4));
    };

    updatePadding();
    const resizeObserver = new ResizeObserver(updatePadding);
    resizeObserver.observe(titleRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [title]);

  return (
    <>
      {title && <FixedTitle ref={titleRef}>{title}</FixedTitle>}
      <div
        style={{
          paddingTop: title ? `${titlePadding}px` : undefined,
          paddingBottom: `${footerHeight}px`,
        }}
      >
        {children}
      </div>
      <FixedFooter ref={footerRef}>{footerContent}</FixedFooter>
    </>
  );
};

const useResponsiveFooterSpacer = (): {
  height: number;
  ref: React.RefObject<HTMLDivElement | null>;
} => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target.clientHeight !== height) {
          setHeight(entry.target.clientHeight);
        }
      }
    });
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return { height, ref };
};
